import IlluminatedLetter from './IlluminatedLetter.jsx';
import Icon from './Icon.jsx';
import { ACTS } from '../data/story.js';

/**
 * The manuscript page.
 *
 * The same twenty-eight chapters as the trail and the map, as a page that
 * illuminates itself. Each chapter you finish turns its capital gold; the rest
 * sit there in outline, so the page always shows both what you have done and
 * how much of it is left, in one look.
 *
 * It exists because progress bars are forgettable and a filling manuscript is
 * not. This is the screenshot someone takes.
 */
export default function StoryIllumination({ story, isPremium, onOpenChapter, onUpgrade }) {
  let order = -1;
  const done = ACTS.flatMap((a) => a.chapters).filter((c) => story[c.id]?.done).length;
  const total = ACTS.reduce((n, a) => n + a.chapters.length, 0);

  return (
    <div className="illumination">
      <div className="illumination__page">
        <header className="illumination__head">
          <p className="illumination__eyebrow">Illuminated</p>
          <h2 className="illumination__title">
            {done} of {total}
          </h2>
          <p className="illumination__sub">
            {done === total
              ? 'The page is finished. Every chapter walked.'
              : 'Each chapter you finish gilds its letter.'}
          </p>
        </header>

        {ACTS.map((act) => (
          <section key={act.id ?? act.title} className="illumination__act">
            <h3 className="illumination__act-title">
              <span>{act.title}</span>
            </h3>

            <div className="illumination__row">
              {act.chapters.map((chapter) => {
                order += 1;
                const earned = !!story[chapter.id]?.done;
                const locked = !isPremium && order >= 4;
                // Three stars per chapter in the trail; the third — answered
                // right first time — is the one worth marking here, because it
                // is the only one you cannot go back and collect.
                const perfect = !!story[chapter.id]?.firstTry;

                return (
                  <button
                    key={chapter.id}
                    type="button"
                    className={`illumination__cell${earned ? ' is-earned' : ''}${
                      locked ? ' is-locked' : ''
                    }`}
                    onClick={() => (locked ? onUpgrade() : onOpenChapter(chapter.id))}
                    title={locked ? 'Premium' : chapter.title}
                  >
                    <IlluminatedLetter
                      letter={chapter.title.trim()[0].toUpperCase()}
                      seed={chapter.id}
                      earned={earned}
                    />
                    <span className="illumination__caption">
                      {locked ? <Icon name="lock" size={11} /> : chapter.title}
                    </span>
                    {perfect ? (
                      <span className="illumination__gem" aria-label="First time">
                        <Icon name="radiance" size={10} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
